import { unzipSync } from 'fflate'

/**
 * Reading shapes out of KML, KMZ and ATAK files, in the browser.
 *
 * The dashboard's copy of the app's importer (app/packages/tacmaps_geo/lib/src/map_import.dart),
 * with the same rules: lenient about structure, strict about coordinates, and every shape left
 * out named with a reason. The two are kept in step by tests that feed both the same files.
 *
 * Walks the tree by local name rather than using namespace lookups. KML arrives in four
 * namespaces or none, with gx: extensions in another, and matching on the local name reads them
 * all the same way in every browser.
 */

export type ImportedKind = 'point' | 'line' | 'area' | 'circle'

export interface LatLon {
  latitude: number
  longitude: number
}

export interface ImportedShape {
  kind: ImportedKind
  /** One for a point or a circle's centre; the vertices otherwise, an area's ring left open. */
  points: LatLon[]
  name?: string
  description?: string
  folder?: string
  /** `#RRGGBB`. */
  colorHex?: string
  strokeWidth?: number
  fillOpacity?: number
  radiusMetres?: number
  /** ATAK markers: friend, hostile, neutral or unknown. */
  affiliation?: string
}

export type MapImportFormat = 'KML' | 'KMZ' | 'ATAK data package' | 'ATAK marker'

export interface MapImport {
  format: MapImportFormat
  fileName: string
  shapes: ImportedShape[]
  skipped: string[]
}

export class MapImportError extends Error {}

export const MAX_SHAPES = 2000
const MAX_ENTRY_BYTES = 16 * 1024 * 1024
const MAX_ARCHIVE_BYTES = 48 * 1024 * 1024

export function parseMapFile(bytes: Uint8Array, fileName: string): MapImport {
  if (bytes.length === 0) throw new MapImportError('The file is empty.')
  if (isZip(bytes)) return parseArchive(bytes, fileName)

  const root = xml(bytes, fileName)
  const found = new Found()
  if (local(root) === 'kml') {
    readKml(root, found)
    return finish('KML', fileName, found)
  }
  if (local(root) === 'event') {
    readCot(root, found)
    return finish('ATAK marker', fileName, found)
  }
  throw new MapImportError(`${fileName} is XML, but not KML or an ATAK marker.`)
}

// --------------------------------------------------------------------------------------- archives

function isZip(bytes: Uint8Array): boolean {
  return bytes.length > 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04
}

function parseArchive(bytes: Uint8Array, fileName: string): MapImport {
  let manifest = false
  let total = 0
  let entries: Record<string, Uint8Array>
  try {
    entries = unzipSync(bytes, {
      // Decided from the declared sizes before anything is inflated: a small zip can claim to
      // unpack to gigabytes, and the check has to happen before the memory is spent.
      filter: (file) => {
        const name = file.name.toLowerCase()
        if (name.endsWith('manifest.xml')) {
          manifest = true
          return false
        }
        if (!(name.endsWith('.kml') || name.endsWith('.cot') || name.endsWith('.xml'))) return false
        if (file.originalSize > MAX_ENTRY_BYTES) {
          throw new MapImportError(`${file.name} inside ${fileName} is too large to import.`)
        }
        total += file.originalSize
        if (total > MAX_ARCHIVE_BYTES) {
          throw new MapImportError(`${fileName} unpacks to more than this page will read.`)
        }
        return true
      },
    })
  } catch (error) {
    if (error instanceof MapImportError) throw error
    throw new MapImportError(`${fileName} could not be opened as a zip archive.`)
  }

  const names = Object.keys(entries)
  // doc.kml first: by convention the main document, and the one Google Earth opens.
  const kml = names
    .filter((name) => name.toLowerCase().endsWith('.kml'))
    .sort((a, b) => Number(b.toLowerCase().endsWith('doc.kml')) - Number(a.toLowerCase().endsWith('doc.kml')))
  const cot = names.filter((name) => !name.toLowerCase().endsWith('.kml'))

  if (kml.length === 0 && cot.length === 0) {
    throw new MapImportError(`${fileName} holds no KML or ATAK markers.`)
  }

  const found = new Found()
  for (const name of kml) readKml(xml(entries[name], name), found)
  for (const name of cot) {
    let root: Element
    try {
      root = xml(entries[name], name)
    } catch {
      found.skip(`${name} is not readable XML`)
      continue
    }
    // An .xml in a package can be anything; only CoT events are markers.
    if (local(root) === 'event') readCot(root, found)
  }

  const format: MapImportFormat = kml.length > 0 ? 'KMZ' : manifest ? 'ATAK data package' : 'ATAK marker'
  return finish(format, fileName, found)
}

function xml(bytes: Uint8Array, name: string): Element {
  let text: string
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(bytes)
  } catch {
    // Latin-1 from an old exporter: read it as it is rather than refusing.
    text = new TextDecoder('latin1').decode(bytes)
  }
  if (text.startsWith('﻿')) text = text.slice(1)
  // CDATA turned into escaped text first. Google writes every description as CDATA, and not every
  // XML parser a page can run under accepts it; escaped text means the same thing to all of them.
  text = text.replace(/<!\[CDATA\[([\s\S]*?)\]\]>/g, (_, content: string) =>
    content.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'),
  )
  // The browser's XML parser fetches nothing external and does not expand external entities.
  const document = new DOMParser().parseFromString(text, 'application/xml')
  if (document.getElementsByTagName('parsererror').length > 0 || !document.documentElement) {
    throw new MapImportError(`${name} is not valid XML.`)
  }
  return document.documentElement
}

function finish(format: MapImportFormat, fileName: string, found: Found): MapImport {
  let shapes = found.shapes
  const skipped = [...found.skipped]
  if (shapes.length > MAX_SHAPES) {
    skipped.push(`${shapes.length - MAX_SHAPES} shapes past the first ${MAX_SHAPES}`)
    shapes = shapes.slice(0, MAX_SHAPES)
  }
  return { format, fileName, shapes, skipped }
}

class Found {
  shapes: ImportedShape[] = []
  skipped: string[] = []
  add(shape: ImportedShape) {
    this.shapes.push(shape)
  }
  skip(reason: string) {
    this.skipped.push(reason)
  }
}

// -------------------------------------------------------------------------------------------- KML

interface Style {
  lineColor?: string
  lineWidth?: number
  polyColor?: string
  polyFill?: boolean
  iconColor?: string
}

const GEOMETRIES = new Set(['Point', 'LineString', 'LinearRing', 'Polygon', 'MultiGeometry', 'Track', 'MultiTrack'])

function readKml(root: Element, found: Found) {
  const styles = new Map<string, Style>()
  const styleMaps = new Map<string, string>()

  for (const style of all(root, 'Style')) {
    const id = style.getAttribute('id')
    if (id) styles.set(id, readStyle(style))
  }
  // A StyleMap pairs a normal and a highlighted style. Only the normal one is ever on screen.
  for (const map of all(root, 'StyleMap')) {
    const id = map.getAttribute('id')
    if (!id) continue
    for (const pair of children(map, 'Pair')) {
      if (text(pair, 'key') === 'normal') {
        const url = text(pair, 'styleUrl')
        if (url) styleMaps.set(id, anchor(url))
      }
    }
  }
  const resolve = (url?: string): Style | undefined => {
    if (!url) return undefined
    const id = anchor(url)
    return styles.get(styleMaps.get(id) ?? id)
  }

  for (const placemark of all(root, 'Placemark')) {
    const name = clip(text(placemark, 'name'), 64)
    const description = plain(text(placemark, 'description'))
    const folder = folderOf(placemark)
    const inline = child(placemark, 'Style')
    const base = resolve(text(placemark, 'styleUrl'))
    const style = inline ? { ...base, ...defined(readStyle(inline)) } : base

    if (!Array.from(placemark.children).some((element) => GEOMETRIES.has(local(element)))) {
      found.skip(`"${name ?? 'Unnamed'}" has no location`)
      continue
    }
    for (const geometry of Array.from(placemark.children)) {
      readGeometry(geometry, found, { name, description, folder }, style)
    }
  }
}

function readStyle(style: Element): Style {
  const line = child(style, 'LineStyle')
  const poly = child(style, 'PolyStyle')
  const icon = child(style, 'IconStyle')
  const fill = poly ? text(poly, 'fill') : undefined
  return {
    lineColor: line ? text(line, 'color') : undefined,
    lineWidth: line ? number(text(line, 'width')) : undefined,
    polyColor: poly ? text(poly, 'color') : undefined,
    polyFill: fill === '0' ? false : fill === '1' ? true : undefined,
    iconColor: icon ? text(icon, 'color') : undefined,
  }
}

interface Labels {
  name?: string
  description?: string
  folder?: string
}

function readGeometry(geometry: Element, found: Found, labels: Labels, style?: Style) {
  const label = labels.name ?? 'Unnamed'
  const shape = (kind: ImportedKind, points: LatLon[]): ImportedShape => ({
    kind,
    points,
    ...defined(labels),
    ...defined({
      colorHex: rgb(kind === 'point' ? style?.iconColor : style?.lineColor),
      strokeWidth: kind === 'point' ? undefined : style?.lineWidth,
      fillOpacity: kind === 'area' ? (style?.polyFill === false ? 0 : alpha(style?.polyColor)) : undefined,
    }),
  })

  switch (local(geometry)) {
    case 'Point': {
      const points = coordinates(text(geometry, 'coordinates'))
      if (!points || points.length === 0) found.skip(`"${label}" has a point with no valid coordinate`)
      else found.add(shape('point', [points[0]]))
      break
    }
    case 'LineString': {
      const points = coordinates(text(geometry, 'coordinates'))
      if (!points || points.length < 2) found.skip(`"${label}" is a line with fewer than two valid points`)
      else found.add(shape('line', points))
      break
    }
    case 'LinearRing':
      ring(text(geometry, 'coordinates'), label, found, (points) => shape('area', points))
      break
    case 'Polygon': {
      // The outer ring only; drawing an area without its hole errs towards more ground, not less.
      const outer = child(geometry, 'outerBoundaryIs')
      const linear = outer ? child(outer, 'LinearRing') : undefined
      ring(linear ? text(linear, 'coordinates') : undefined, label, found, (points) => shape('area', points))
      break
    }
    case 'MultiGeometry':
      for (const part of Array.from(geometry.children)) readGeometry(part, found, labels, style)
      break
    case 'Track': {
      const points: LatLon[] = []
      for (const coord of children(geometry, 'coord')) {
        const parts = (coord.textContent ?? '').trim().split(/\s+/)
        const point = parts.length >= 2 ? at(parts[0], parts[1]) : undefined
        if (point) points.push(point)
      }
      if (points.length < 2) found.skip(`"${label}" is a track with fewer than two valid points`)
      else found.add(shape('line', points))
      break
    }
    case 'MultiTrack':
      for (const track of children(geometry, 'Track')) readGeometry(track, found, labels, style)
      break
  }
}

function ring(raw: string | undefined, label: string, found: Found, build: (points: LatLon[]) => ImportedShape) {
  const points = coordinates(raw)
  if (!points) {
    found.skip(`"${label}" is an area with an invalid coordinate`)
    return
  }
  // KML closes a ring by repeating the first point; areas here close themselves.
  if (points.length > 1 && same(points[0], points[points.length - 1])) points.pop()
  if (points.length < 3) {
    found.skip(`"${label}" is an area with fewer than three corners`)
    return
  }
  found.add(build(points))
}

function folderOf(placemark: Element): string | undefined {
  for (let node = placemark.parentElement; node; node = node.parentElement) {
    if (local(node) === 'Folder') return clip(text(node, 'name'), 64)
  }
  return undefined
}

/** `lon,lat[,alt]` tuples. All or nothing: a line missing one vertex is a different line. */
function coordinates(raw?: string): LatLon[] | undefined {
  if (raw === undefined) return undefined
  const tuples = raw.trim().replace(/\s*,\s*/g, ',').split(/\s+/)
  const points: LatLon[] = []
  for (const tuple of tuples) {
    if (!tuple) continue
    const parts = tuple.split(',')
    if (parts.length < 2) return undefined
    const point = at(parts[0], parts[1])
    if (!point) return undefined
    points.push(point)
  }
  return points
}

// ------------------------------------------------------------------------------------- ATAK / CoT

function readCot(event: Element, found: Found) {
  const type = event.getAttribute('type') ?? ''
  const detail = child(event, 'detail')
  const name = clip(
    (detail && child(detail, 'contact')?.getAttribute('callsign')) || event.getAttribute('uid') || undefined,
    64,
  )
  const label = name ?? 'Unnamed'
  const description = detail ? plain(text(detail, 'remarks')) : undefined
  const stroke = detail ? argb(child(detail, 'strokeColor')?.getAttribute('value')) : undefined
  const marker = detail ? argb(child(detail, 'color')?.getAttribute('argb')) : undefined
  const fill = detail ? child(detail, 'fillColor')?.getAttribute('value') : undefined
  const weight = detail ? number(child(detail, 'strokeWeight')?.getAttribute('value') ?? undefined) : undefined

  const links: LatLon[] = []
  if (detail) {
    for (const link of children(detail, 'link')) {
      const raw = link.getAttribute('point')
      if (!raw) continue
      // CoT's own order, latitude first: the reverse of KML's.
      const parts = raw.split(',')
      const point = parts.length >= 2 ? at(parts[1].trim(), parts[0].trim()) : undefined
      if (point) links.push(point)
    }
  }
  const pointElement = child(event, 'point')
  const centre = pointElement
    ? at(pointElement.getAttribute('lon') ?? '', pointElement.getAttribute('lat') ?? '')
    : undefined

  const shape = (kind: ImportedKind, points: LatLon[], radius?: number): ImportedShape => ({
    kind,
    points,
    ...defined({
      name,
      description,
      colorHex: kind === 'point' ? (marker ?? stroke) : (stroke ?? marker),
      strokeWidth: kind === 'point' ? undefined : weight,
      fillOpacity: kind === 'area' || kind === 'circle' ? argbAlpha(fill) : undefined,
      radiusMetres: radius,
      affiliation: type.startsWith('a-') ? affiliationOf(type) : undefined,
    }),
  })

  if (type.startsWith('u-d-c-c')) {
    const ellipse = detail ? all(detail, 'ellipse')[0] : undefined
    const radius = number(ellipse?.getAttribute('major') ?? undefined)
    if (!centre || !radius || radius <= 0) found.skip(`"${label}" is a circle without a centre or radius`)
    else found.add(shape('circle', [centre], radius))
  } else if (type.startsWith('u-d-r')) {
    if (links.length < 3) found.skip(`"${label}" is a rectangle without its corners`)
    else found.add(shape('area', links.slice(0, 4)))
  } else if (links.length >= 2) {
    // Freehand shapes, routes, anything drawn as points. One that ends where it began is an area.
    const closed = links.length >= 4 && same(links[0], links[links.length - 1])
    found.add(closed ? shape('area', links.slice(0, -1)) : shape('line', links))
  } else if (centre) {
    found.add(shape('point', [centre]))
  } else {
    found.skip(`"${label}" has no location`)
  }
}

function affiliationOf(type: string): string {
  switch (type.split('-')[1] ?? '') {
    case 'f':
    case 'a':
      return 'friend'
    case 'h':
    case 's':
    case 'j':
    case 'k':
      return 'hostile'
    case 'n':
      return 'neutral'
    default:
      return 'unknown'
  }
}

/** ATAK writes colours as a signed 32-bit ARGB integer, so opaque red is -65536. */
function argb(raw?: string | null): string | undefined {
  if (raw == null || !/^-?\d+$/.test(raw.trim())) return undefined
  const rgbValue = Number(raw) & 0xffffff
  return `#${rgbValue.toString(16).padStart(6, '0').toUpperCase()}`
}

function argbAlpha(raw?: string | null): number | undefined {
  if (raw == null || !/^-?\d+$/.test(raw.trim())) return undefined
  return ((Number(raw) >>> 24) & 0xff) / 255
}

// ----------------------------------------------------------------------------------------- shared

function at(lon: string, lat: string): LatLon | undefined {
  if (lon.trim() === '' || lat.trim() === '') return undefined
  const longitude = Number(lon)
  const latitude = Number(lat)
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) return undefined
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return undefined
  return { latitude, longitude }
}

function same(a: LatLon, b: LatLon): boolean {
  return a.latitude === b.latitude && a.longitude === b.longitude
}

/** KML colour, `aabbggrr`, as `#RRGGBB`. */
function rgb(kml?: string): string | undefined {
  const hex = kml?.trim().replace('#', '')
  if (!hex || !/^[0-9a-fA-F]{8}$/.test(hex)) return undefined
  return `#${hex.slice(6, 8)}${hex.slice(4, 6)}${hex.slice(2, 4)}`.toUpperCase()
}

function alpha(kml?: string): number | undefined {
  const hex = kml?.trim().replace('#', '')
  if (!hex || !/^[0-9a-fA-F]{8}$/.test(hex)) return undefined
  return parseInt(hex.slice(0, 2), 16) / 255
}

function plain(html?: string): string | undefined {
  if (html === undefined) return undefined
  const value = html
    .replace(/<br ?\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+/g, ' ')
    .replace(/\n\s*\n+/g, '\n')
    .trim()
  return clip(value, 2000)
}

function clip(value: string | undefined, max: number): string | undefined {
  const trimmed = value?.trim()
  if (!trimmed) return undefined
  return trimmed.length <= max ? trimmed : `${trimmed.slice(0, max - 1)}…`
}

function anchor(url: string): string {
  return url.includes('#') ? url.slice(url.indexOf('#') + 1) : url
}

function number(raw?: string): number | undefined {
  if (raw === undefined || raw.trim() === '') return undefined
  const value = Number(raw)
  return Number.isFinite(value) ? value : undefined
}

/** Drops undefined values, so spreading a partial over a full object keeps the full one's. */
function defined<T extends object>(value: T): Partial<T> {
  return Object.fromEntries(Object.entries(value).filter(([, v]) => v !== undefined)) as Partial<T>
}

const local = (element: Element) => element.localName ?? element.nodeName.replace(/^.*:/, '')

function all(root: Element, name: string): Element[] {
  const found: Element[] = []
  const walk = (element: Element) => {
    for (const childElement of Array.from(element.children)) {
      if (local(childElement) === name) found.push(childElement)
      walk(childElement)
    }
  }
  walk(root)
  return found
}

const children = (parent: Element, name: string) => Array.from(parent.children).filter((c) => local(c) === name)

const child = (parent: Element, name: string): Element | undefined => children(parent, name)[0]

function text(parent: Element, name: string): string | undefined {
  const value = child(parent, name)?.textContent?.trim()
  return value ? value : undefined
}
